import AddIcon from "@mui/icons-material/Add";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import React, { useState } from "react";
import { toast } from "react-toastify";

import BenevvTextField from "components/forms/BenevvTextField/BenevvTextField";
import CausesSelector from "components/forms/CausesSelector/CausesSelector";

import { useAppDispatch, useAppSelector } from "store/hooks";

import MultiStepForm, {
  StepDefinition,
} from "components/forms/MultiStepForm/MultiStepForm";
import { useNavigate, useParams } from "react-router-dom";
import { CauseId } from "types";
import {
  CenterPageSection,
  Sections,
} from "views/ProjectManagement/Program/Projects/Project.styles";
import {
  DateCol,
  DatePickerContainer,
  DateSeparator,
  DatesRow,
  FieldsColumn,
  InputDatePickerField,
  Label,
  LocationContainer,
  OngoingContainer,
  OrContainer,
  Required
} from "./CreateProject.styles";
import { selectProjectFormState } from "store/projects/projectForm/projectForm.selectors";
import { createProjectThunk } from "store/projects/projectForm/projectForm.actions";

const CreateProject = () => {
  const { programId } = useParams();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { statusState } = useAppSelector(selectProjectFormState);

  const [projectName, setProjectName] = useState("");
  const [projectDescription, setProjectDescription] = useState("");
  const [startTime, setStartTime] = useState("");
  const [endTime, setEndTime] = useState("");
  const [ongoing, setOngoing] = useState(false);
  const [location, setLocation] = useState("");
  const [selectedCauses, setSelectedCauses] = useState<CauseId[]>([]);

  const baseProjectData = {
    project_name: projectName,
    project_description: projectDescription,
    start_date_time: startTime,
    end_date_time: endTime,
    location,
    causeIds: selectedCauses,
    activityIds: [],
    postIds: [],
    program_id: Number(programId) ?? null,
  };

  const handleReturn = () => {
    navigate(-1);
  };

  const isStep1Complete =
    projectName.trim() !== "" && projectDescription.trim() !== "";
  const isStep2Complete =
    startTime.trim() !== "" && (endTime.trim() !== "" || ongoing);
  const isStep3Complete = location.trim() !== "";
  const isStep4Complete = selectedCauses.length > 0;

  const handleOngoing = (e: React.MouseEvent) => {
    e.preventDefault();
    setOngoing(!ongoing);
    if (!ongoing) setEndTime("");
  };

  const handleSubmit = async () => {
    const result = await dispatch(createProjectThunk(baseProjectData));
    if (createProjectThunk.fulfilled.match(result)) {
      toast.success("Project created successfully!");
      handleReturn();
    } else {
      toast.error("Error creating project: " + (result.payload as string));
    }
  };

  const steps: StepDefinition[] = [
    {
      title: "Project basic info",
      render: (disabled) => (
        <>
          <FieldsColumn>
            <BenevvTextField
              required
              htmlFor="projectName"
              labelText="Project name *"
              placeholder="Enter project name"
              setTextField={setProjectName}
              disabled={disabled}
            />
          </FieldsColumn>

          <FieldsColumn>
            <BenevvTextField
              required
              htmlFor="projectDescription"
              labelText="Project description *"
              placeholder="Enter project description"
              setTextField={setProjectDescription}
              disabled={disabled}
            />
          </FieldsColumn>
        </>
      ),
      validate: () => isStep1Complete,
    },
    {
      title: "Date",
      render: (disabled) => (
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <DatePickerContainer style={{ marginBottom: "10px" }}>
            <DatesRow>
              <DateCol>
                <Label>
                  Project starts <Required>*</Required>
                </Label>
                <InputDatePickerField
                  type="datetime-local"
                  value={startTime}
                  onChange={(e) => setStartTime(e.target.value)}
                  min={new Date().toISOString().slice(0, 16)}
                />
              </DateCol>
              <DateSeparator>–</DateSeparator>
              <DateCol>
                <Label>
                  Project ends <Required>*</Required>
                </Label>
                <InputDatePickerField
                  type="datetime-local"
                  value={endTime}
                  onChange={(e) => setEndTime(e.target.value)}
                  min={startTime || new Date().toISOString().slice(0, 16)}
                />
              </DateCol>
            </DatesRow>

            <div className="orBlock">
              <OrContainer>or</OrContainer>
            </div>

            <OngoingContainer disabled={disabled} onClick={handleOngoing}>
              {ongoing ? "Ongoing ✓" : "Ongoing"}
            </OngoingContainer>
          </DatePickerContainer>
        </LocalizationProvider>
      ),
      validate: () => isStep2Complete,
    },
    {
      title: "Location(s)",
      render: (disabled) => (
        <>
          <BenevvTextField
            required
            htmlFor="projectLocation"
            labelText="Project location *"
            placeholder="Enter project location"
            setTextField={setLocation}
            disabled={disabled}
          />

          <LocationContainer
            style={{
              color: disabled ? "#ccc" : "#fe2f67",
              cursor: disabled ? "not-allowed" : "pointer",
            }}
          >
            <AddIcon
              fontSize="small"
              sx={{
                verticalAlign: "middle",
                fontSize: "0.7rem",
                marginBottom: "1px",
                cursor: disabled ? "not-allowed" : "pointer",
              }}
            />
            Add Another Location
          </LocationContainer>
        </>
      ),
      validate: () => isStep3Complete,
    },
    {
      title: "Cause(s)",
      render: (disabled) => (
        <div style={{ opacity: disabled ? 0.5 : 1 }}>
          <CausesSelector selectedCauses={selectedCauses} setSelectedCauses={setSelectedCauses} disabled={disabled} border={{
            borderColor: "#8692a6",
            borderStyle: "solid",
            borderWidth: "0.0625rem"
          }}/>
        </div>
      ),
      validate: () => isStep4Complete,
    },
  ];

  return (
    <Sections>
      <CenterPageSection>
        <MultiStepForm
          title="Create a project"
          steps={steps}
          onBack={handleReturn}
          onSubmit={handleSubmit}
          submitLabel={statusState === "loading" ? "Saving..." : "Create"}
        />
      </CenterPageSection>
    </Sections>
  );
};

export default CreateProject;
